import { Fragment, useState, useEffect } from 'react'
import { NavLink, useLocation, useSearchParams } from 'react-router-dom'
import { Popover, Transition, Switch } from '@headlessui/react'
import clsx from 'clsx'
import { useInView } from 'react-intersection-observer'
import { useMutation, useReactiveVar } from '@apollo/client'
import { toast } from 'react-toastify'

import {
  apolloAuthData,
  apolloEditStatusData,
  getEditStatus,
  resetEditStatus,
  setPublishedEditStatus,
} from '../services/apollo'
import { getProfilePic, generateMutationVariables } from '../utils'
import { setAuthData } from '../hooks/useAuth'
import { ROLE_MANAGER } from '../constants'
import { ConfirmDialog, Rollback } from '../components'
import {
  UPDATE_HOME_PAGE_MUTATION,
  UPDATE_AGENDA_PAGE_MUTATION,
  UPDATE_AGENDA_ITEM_MUTATION,
  UPDATE_TIPSTOOLS_PAGE_MUTATION,
  UPDATE_TIPSTOOLS_ITEM_MUTATION,
  DELETE_TIPSTOOLS_ITEM_MUTATION,
  UPDATE_LEARNING_LOUNGE_PAGE_MUTATION,
  UPDATE_LEARNING_LOUNGE_ITEM_MUTATION,
  CREATE_LEARNING_LOUNGE_ITEM_MUTATION,
  DELETE_LEARNING_LOUNGE_ITEM_MUTATION,
} from '../services/admin'

import logo from '../assets/images/logo.png'
import { ReactComponent as Curve2 } from '../assets/images/curve-2.svg'
import { ReactComponent as ProfileIcon } from '../assets/icons/profile-icon.svg'

const Header = () => {
  const [confirmPublish, setConfirmPublish] = useState(false)
  const [editEnabled, setEditEnabled] = useState(false)
  const auth = useReactiveVar(apolloAuthData)
  const editStatus = useReactiveVar(apolloEditStatusData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  let location = useLocation()
  let [searchParams, setSearchParams] = useSearchParams()
  const isEditOn = isManager && !!searchParams.get('edit')
  const isHomePage = location.pathname === '/'
  const [updateHomePage, { loading: updatingHomePage }] = useMutation(
    UPDATE_HOME_PAGE_MUTATION
  )
  const [updateAgendaPage, { loading: updatingAgendaPage }] = useMutation(
    UPDATE_AGENDA_PAGE_MUTATION
  )
  const [updateAgendaItem, { loading: updatingAgendaItem }] = useMutation(
    UPDATE_AGENDA_ITEM_MUTATION
  )
  const [updateTipsToolsPage, { loading: updatingTipsToolsPage }] = useMutation(
    UPDATE_TIPSTOOLS_PAGE_MUTATION
  )
  const [deleteTipsToolsItem, { loading: deletingTipsToolsItem }] = useMutation(
    DELETE_TIPSTOOLS_ITEM_MUTATION
  )
  const [updateTipsToolsItem, { loading: updatingTipsToolsItem }] = useMutation(
    UPDATE_TIPSTOOLS_ITEM_MUTATION
  )
  const [updateLearningLoungePage, { loading: updatingLearningLoungePage }] =
    useMutation(UPDATE_LEARNING_LOUNGE_PAGE_MUTATION)
  const [createLearningLoungeItem, { loading: creatingLearningLoungeItem }] =
    useMutation(CREATE_LEARNING_LOUNGE_ITEM_MUTATION)
  const [updateLearningLoungeItem, { loading: updatingLearningLoungeItem }] =
    useMutation(UPDATE_LEARNING_LOUNGE_ITEM_MUTATION)
  const [deleteLearningLoungeItem, { loading: deletingLearningLoungeItem }] =
    useMutation(DELETE_LEARNING_LOUNGE_ITEM_MUTATION)
  const { ref, inView, entry } = useInView({
    threshold: 0,
  })
  let navList = isManager
    ? [
        {
          to: '/admin/new',
          text: 'admin',
        },
      ]
    : []
  navList.push(
    { to: '/agenda/preparation', text: 'preparation and session materials' },
    { to: '/tipstools', text: 'tips & tools' },
    { to: '/learninglounge', text: 'learning lounge' },
    { to: '/leaderboard', text: 'leaderboard' }
  )

  useEffect(() => {
    console.log('editStatus.changes', editStatus.changes)
  }, [editStatus.changes])

  /**
   * Reload profile pic when page is reloaded
   * Blob image does expires from time to time
   */
  useEffect(() => {
    if (auth.user?.profilePic) {
      getProfilePic(auth.user.profilePic)
        .then((profilePicURL) => {
          if (profilePicURL) {
            setAuthData({
              ...apolloAuthData(),
              user: { ...apolloAuthData().user, profilePicURL },
            })
          } else {
            throw new Error()
          }
        })
        .catch(() => {
          setAuthData({
            ...apolloAuthData(),
            user: {
              ...apolloAuthData().user,
              profilePicURL: '',
              profilePic: '',
            },
          })
        })
    }
  }, [auth.user?.profilePic])

  /**
   * Toggle edit switch based on search params
   */
  useEffect(() => {
    if (searchParams.get('edit') === 'true') {
      setEditEnabled(true)
    } else if (searchParams.get('edit') === null) {
      setEditEnabled(false)
    }
  }, [searchParams])

  /**
   * Always reset edit status when edit switch is off
   */
  useEffect(() => {
    if (!isEditOn) {
      resetEditStatus()
    }
  }, [isEditOn])

  /**
   * Handle edit switch onChange event
   *
   * @param {boolean} value - `true` if the switch is on; Otherwise, it's `false`.
   */
  const handleEditSwitch = (value) => {
    if (value === true) {
      setSearchParams({ edit: true })
    } else {
      if (searchParams.get('edit') !== null) {
        setSearchParams({})
      }
    }
  }

  /**
   * Open confirm dialog box before publishing
   */
  const handlePublishClick = () => {
    setConfirmPublish(true)
  }

  /**
   * User cancels the confirmation.
   *
   * Close the dialog box
   */
  const handleConfirmPublishCancel = () => {
    setConfirmPublish(false)
  }

  /**
   * Handle publishing the content
   */
  const handlePublish = async () => {
    const editStatus = getEditStatus()
    let tasks = []

    // Prepare data
    if (Array.isArray(editStatus.changes)) {
      const [pageVariables, itemVariables] =
        generateMutationVariables(editStatus)

      console.log('pageVariables', pageVariables)
      console.log('itemVariables', itemVariables)

      if (pageVariables.page === 'HOME_PAGE') {
        tasks.push(
          updateHomePage({
            variables: pageVariables.variables,
          })
        )
      }
      if (pageVariables.page === 'AGENDA_PAGE') {
        tasks.push(
          updateAgendaPage({
            variables: pageVariables.variables,
          })
        )
      }
      if (pageVariables.page === 'TIPS_TOOLS_PAGE') {
        tasks.push(
          updateTipsToolsPage({
            variables: pageVariables.variables,
          })
        )
      }
      if (pageVariables.page === 'LEARNING_LOUNGE_PAGE') {
        tasks.push(
          updateLearningLoungePage({
            variables: pageVariables.variables,
          })
        )
      }

      const deleteItemVariables = itemVariables
        .filter(
          (itemVariable) =>
            itemVariable.action === 'remove' &&
            typeof itemVariable.variables.id === 'number'
        )
        .map((itemVariable) => {
          // Remove `action` field
          const { action, ...rest } = itemVariable.variables

          return { ...itemVariable, variables: rest }
        })
      const createItemVariables = itemVariables
        .filter((itemVariable) => itemVariable.action === 'create')
        .map((itemVariable) => {
          // Remove `action` field
          const { action, ...rest } = itemVariable.variables

          return { ...itemVariable, variables: rest }
        })
      const updateItemVariables = itemVariables
        .filter(
          (itemVariable) =>
            itemVariable.action === 'update' &&
            typeof itemVariable.variables.id === 'number'
        )
        .map((itemVariable) => {
          // Remove `action` field
          const { action, ...rest } = itemVariable.variables

          return { ...itemVariable, variables: rest }
        })

      console.log('>>> createItemVariables', createItemVariables)
      console.log('>>> deleteItemVariables', deleteItemVariables)
      console.log('>>> updateItemVariables', updateItemVariables)

      // Create create mutation tasks
      createItemVariables.forEach((itemVariable) => {
        if (itemVariable.page === 'LEARNING_LOUNGE_ITEM') {
          tasks.push(
            createLearningLoungeItem({
              variables: itemVariable.variables,
            })
          )
        }
      })

      // Create `update` mutation tasks
      updateItemVariables.forEach((itemVariable) => {
        if (itemVariable.page === 'AGENDA_ITEM') {
          tasks.push(
            updateAgendaItem({
              variables: itemVariable.variables,
            })
          )
        } else if (itemVariable.page === 'TIPS_TOOLS_ITEM') {
          tasks.push(
            updateTipsToolsItem({
              variables: { ...itemVariable.variables, status: 'PUBLISH' },
            })
          )
        } else if (itemVariable.page === 'LEARNING_LOUNGE_ITEM') {
          tasks.push(
            updateLearningLoungeItem({
              variables: { ...itemVariable.variables },
            })
          )
        }
      })

      // Create `delete` mutation tasks
      deleteItemVariables.forEach((itemVariable) => {
        if (itemVariable.page === 'TIPS_TOOLS_ITEM') {
          tasks.push(
            deleteTipsToolsItem({
              variables: itemVariable.variables,
            })
          )
        } else if (itemVariable.page === 'LEARNING_LOUNGE_ITEM') {
          tasks.push(
            deleteLearningLoungeItem({
              variables: itemVariable.variables,
            })
          )
        }
      })

      if (tasks.length > 0) {
        try {
          const taskResponses = await Promise.all(tasks)
          console.log('taskResponses', taskResponses)
          toast.success(`Your changes have been published successfully`)

          // Clear the edit status and mark the published status
          setPublishedEditStatus()
        } catch (err) {
          console.error('handlePublish :: err', err)
          toast.error(
            `Sorry, your changes weren't published. Please try again!`
          )
        } finally {
          setConfirmPublish(false)
        }
      }

      setConfirmPublish(false)
    }
  }

  const isEditablePage =
    location.pathname.startsWith('/agenda') ||
    location.pathname.startsWith('/learninglounge') ||
    ['/', '/tipstools'].includes(location.pathname)

  return (
    <>
      {/** Publish confirmation dialog */}
      {isEditOn && (
        <ConfirmDialog
          yesText="Publish"
          noText="Cancel"
          open={confirmPublish}
          inProgress={
            updatingHomePage ||
            updatingAgendaPage ||
            updatingAgendaItem ||
            updatingTipsToolsPage ||
            updatingTipsToolsItem ||
            deletingTipsToolsItem ||
            updatingLearningLoungePage ||
            updatingLearningLoungeItem ||
            creatingLearningLoungeItem ||
            deletingLearningLoungeItem
          }
          inProgressButtonText="Publishing..."
          onConfirm={handlePublish}
          onCancel={handleConfirmPublishCancel}
          text="<p>Are you sure you want to publish your changes?</p>"
        />
      )}
      {/** Dummy header for detecting the scroll */}
      <div
        ref={ref}
        className="absolute -top-[35px] left-0 z-30 h-[70px] w-full"
      ></div>

      {/** Header nav */}
      <header
        className={clsx(
          'top-0 z-40',
          isHomePage ? 'fixed left-0 w-full' : 'sticky'
        )}
      >
        <div
          className={clsx(
            'relative flex items-center justify-between px-3 py-4 text-white md:px-6 lg:px-16',
            isHomePage && 'transition-colors duration-200',
            isHomePage ? entry && !inView && 'bg-skin-fill' : 'bg-skin-fill'
          )}
        >
          <NavLink to="/">
            <img className="w-[104px]" src={logo} alt="logo" />
          </NavLink>
          {auth.user ? (
            <>
              {/** Edit page switch */}
              {isManager && isEditablePage && (
                <div className="ml-6 flex space-x-4">
                  <div className="flex items-center justify-center">
                    <span className="mr-2">edit</span>
                    <Switch
                      checked={editEnabled}
                      onChange={handleEditSwitch}
                      className={clsx(
                        editEnabled ? 'bg-beer' : 'bg-dark-gray',
                        'relative inline-flex h-[26px] w-[46px] items-center rounded-full ease-in-out'
                      )}
                    >
                      <span className="sr-only">Edit page</span>
                      <span
                        className={`${
                          editEnabled
                            ? 'translate-x-[22px]'
                            : 'translate-x-[2px]'
                        } inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out`}
                      />
                    </Switch>
                  </div>
                  {editEnabled &&
                    !location.pathname.match(/tipstools\/\w+/) && (
                      <button
                        disabled={
                          editStatus?.changes?.length === 0 ||
                          updatingHomePage ||
                          updatingAgendaPage ||
                          updatingAgendaItem ||
                          updatingTipsToolsPage ||
                          updatingTipsToolsItem ||
                          deletingTipsToolsItem ||
                          updatingLearningLoungePage ||
                          updatingLearningLoungeItem ||
                          creatingLearningLoungeItem ||
                          deletingLearningLoungeItem
                        }
                        className={clsx(
                          'rounded-full px-3 py-1 text-xs leading-none transition-colors',
                          editStatus?.changes?.length > 0 &&
                            !updatingHomePage &&
                            !updatingAgendaPage &&
                            !updatingAgendaItem &&
                            !updatingTipsToolsPage &&
                            !updatingTipsToolsItem &&
                            !deletingTipsToolsItem &&
                            !updatingLearningLoungePage &&
                            !updatingLearningLoungeItem &&
                            !creatingLearningLoungeItem &&
                            !deletingLearningLoungeItem
                            ? 'bg-skin-button-accent text-white hover:bg-skin-button-accent-hover active:translate-y-[0.75px] active:scale-[0.99]'
                            : 'cursor-not-allowed bg-bright-gray text-dark-charcoal opacity-50'
                        )}
                        onClick={handlePublishClick}
                      >
                        Publish
                      </button>
                    )}
                  {editEnabled && <Rollback />}
                </div>
              )}
              <nav className="ml-6 flex items-center xl:ml-0">
                <Popover className="relative">
                  <Popover.Button>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-6 w-6 transition-colors md:hidden"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.75 9h16.5m-16.5 6.75h16.5"
                      />
                    </svg>
                  </Popover.Button>
                  <Popover.Overlay className="fixed inset-0 bg-black opacity-30" />

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute right-0 z-10">
                      <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="relative grid w-[200px] gap-2 bg-white py-4">
                          {navList
                            .concat({ to: 'profile', text: 'your profile' })
                            .map((link) => (
                              <Popover.Button
                                key={link.to}
                                as={NavLink}
                                to={link.to}
                                className={({ isActive }) =>
                                  `group flex w-full items-center rounded-md px-6 text-sm font-bold text-dark-charcoal [&.active]:underline`
                                }
                              >
                                {link.text}
                              </Popover.Button>
                            ))}
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </Popover>

                <ul className="nostyle hidden items-center gap-6 md:flex lg:gap-6 xl:gap-9">
                  <li className="ml-6 hidden xl:ml-6 xl:list-item">
                    <span className="inline-block h-0 w-[80px] border-b-[3px] border-t-[3px] border-b-transparent"></span>
                  </li>
                  {navList
                    .concat({
                      to: 'profile',
                      text: auth.user?.profilePicURL ? (
                        <img
                          src={auth.user.profilePicURL}
                          alt=""
                          className="h-[38px] w-[38px] rounded-full bg-white"
                        />
                      ) : (
                        <ProfileIcon className="h-[38px] w-[38px]" />
                      ),
                    })
                    .map((link) => (
                      <li key={link.to}>
                        <NavLink
                          to={link.to}
                          className={({ isActive }) =>
                            `font-bold ${isActive ? 'underline' : ''}`
                          }
                        >
                          {link.text}
                        </NavLink>
                      </li>
                    ))}
                </ul>
              </nav>
            </>
          ) : (
            <nav>
              <NavLink to="/login" className="font-bold">
                Login
              </NavLink>
            </nav>
          )}
        </div>
        {!isHomePage && (
          <Curve2
            preserveAspectRatio={'none'}
            className={clsx('duration-200 [&_path]:!fill-skin-fill')}
          />
        )}
      </header>
    </>
  )
}

export default Header
